import dynamic from 'next/dynamic';
import { queryCache, useMutation, useQuery } from 'react-query';

import Icon from 'shopper/components/Icon';

import { sendEvent } from 'lib/gtag';
import { wishlistQueryKeys } from 'lib/queryKeys';
import { del, get, post } from 'lib/request';

import { useLocalWishlist } from 'providers/LocalWishlistProvider';
import { useModal } from 'providers/ModalProvider';
import { useUser } from 'providers/UserProvider';

import useToast from './useToast';

import WISHLIST from 'constants/wishlist';

const LoginModal = dynamic(() => import('components/LoginModal'), {
  ssr: false,
});

const MAX_ACTION_UNDO_TIME_IN_SECONDS = 3;
const MESSAGE_ERROR_MESSAGE_KEYWORD_ALREADY_ADDED = 'already';
const MESSAGE_ERROR_MESSAGE_INVALID_KEYWORD = 'INVALID_KEYWORD';

const postAddKeyword = ({ checkGeneric, keywordDisplay, keywordMax }) => {
  const body = { keywordTherm: keywordDisplay, checkGeneric };

  if (keywordMax) {
    body.keywordMax = keywordMax;
  }

  return post('/wishlist/offer/keyword', { body });
};

const deleteKeyword = (keywordId) =>
  del(`/wishlist/offer/keyword/${keywordId}`);

function useWishlist(
  gaEventCategory,
  {
    gaEventLabel,
    showToastOnMutateSuccess = true,
    onAddKeywordToWishlistSuccess = () => {},
  } = {}
) {
  const { showToast } = useToast();
  const { showModal } = useModal();
  const { isLoggedIn, LoggedInUser } = useUser();
  const {
    addLocalOfferKeyword,
    removeLocalOfferKeyword,
    withLocalWishlistItems,
  } = useLocalWishlist();

  const { data: offerWishlist } = useQuery(
    wishlistQueryKeys.offerKeywords(),
    async () =>
      isLoggedIn
        ? get('/wishlist/offer/keyword')
        : withLocalWishlistItems(await get('/wishlist/offer/keyword'))
  );

  const getOfferKeywordId = (keywordDisplay) =>
    offerWishlist?.items.find((item) => item.keywordDisplay === keywordDisplay)
      ?.keywordId;

  const [mutateAddKeyword] = useMutation(
    ({ checkGeneric, keywordDisplay, keywordMax }) =>
      isLoggedIn
        ? postAddKeyword({ checkGeneric, keywordDisplay, keywordMax })
        : addLocalOfferKeyword(keywordDisplay),
    {
      onError: ({ message }) => {
        if (isLoggedIn) {
          showToast({
            iconLeft: <Icon name="info-sign" />,
            children:
              message === MESSAGE_ERROR_MESSAGE_KEYWORD_ALREADY_ADDED &&
              message !== MESSAGE_ERROR_MESSAGE_INVALID_KEYWORD
                ? WISHLIST.TOAST.KEYWORD_ALREADY_ADDED
                : WISHLIST.TOAST.ERROR_WHEN_ATTEMPTING_ADD_KEYWORD,
            secondsToAutoClose: MAX_ACTION_UNDO_TIME_IN_SECONDS,
          });
        }
      },
      onMutate: () => {
        sendEvent({
          action: isLoggedIn
            ? 'keyword_add_attempt'
            : 'keyword_add_attempt_unlogged',
          category: gaEventCategory,
          label: gaEventLabel,
        });

        if (!isLoggedIn) {
          showModal(LoginModal, { isOpen: true });
        }
      },
      onSuccess: (
        keyword,
        { mutateRemoveKeyword, keywordDisplay, showToastOnSuccess = true }
      ) => {
        sendEvent({
          action: isLoggedIn ? 'keyword_added' : 'keyword_added_unlogged',
          category: gaEventCategory,
          label: gaEventLabel,
        });

        queryCache.setQueryData(
          wishlistQueryKeys.offerKeywords({
            userId: LoggedInUser?.userId ?? null,
          }),
          {
            ...offerWishlist,
            items: [
              isLoggedIn
                ? keyword
                : {
                    keywordDisplay,
                    keywordMax: -1,
                    keywordMin: 0,
                    keywordCategoryId: 0,
                    keywordTop: false,
                  },
              ...offerWishlist.items,
            ],
            suggestions: offerWishlist.suggestions.filter(
              (suggestion) => suggestion !== keywordDisplay
            ),
          }
        );

        if (isLoggedIn && showToastOnSuccess && showToastOnMutateSuccess) {
          showToast({
            children: WISHLIST.TOAST.KEYWORD_ADDED_WITH_SUCCESSFULLY,
            type: 'action',
            secondsToAutoClose: MAX_ACTION_UNDO_TIME_IN_SECONDS,
            linkProps: {
              children: 'Desfazer',
              onClick: () => {
                sendEvent({
                  action: isLoggedIn
                    ? 'undo_added_keyword'
                    : 'undo_added_keyword_unlogged',
                  category: gaEventCategory,
                  label: gaEventLabel,
                });
                mutateRemoveKeyword({
                  keywordDisplay,
                  showToastOnSuccess: false,
                });
              },
            },
          });
        }

        onAddKeywordToWishlistSuccess();
      },
    }
  );

  const [mutateRemoveKeyword] = useMutation(
    ({ keywordDisplay }) =>
      isLoggedIn
        ? deleteKeyword(getOfferKeywordId(keywordDisplay))
        : removeLocalOfferKeyword(keywordDisplay),
    {
      onError: () => {
        showToast({
          children: WISHLIST.TOAST.ERROR_WHEN_ATTEMPTING_REMOVE_KEYWORD,
          secondsToAutoClose: MAX_ACTION_UNDO_TIME_IN_SECONDS,
        });
      },
      onMutate: () => {
        sendEvent({
          action: 'keyword_remove_attempt',
          category: gaEventCategory,
          label: gaEventLabel,
        });
      },
      onSuccess: (_, { keywordDisplay, showToastOnSuccess = true }) => {
        sendEvent({
          action: isLoggedIn ? 'keyword_removed' : 'keyword_removed_unlogged',
          category: gaEventCategory,
          label: gaEventLabel,
        });

        queryCache.setQueryData(
          wishlistQueryKeys.offerKeywords({
            userId: LoggedInUser?.userId ?? null,
          }),
          {
            ...offerWishlist,
            items: offerWishlist.items.filter(
              (item) => item.keywordDisplay !== keywordDisplay
            ),
          }
        );

        if (isLoggedIn && showToastOnSuccess && showToastOnMutateSuccess) {
          showToast({
            children: WISHLIST.TOAST.KEYWORD_REMOVED_WITH_SUCCESSFULLY,
            type: 'action',
            secondsToAutoClose: MAX_ACTION_UNDO_TIME_IN_SECONDS,
            linkProps: {
              children: 'Desfazer',
              onClick: () => {
                sendEvent({
                  action: isLoggedIn
                    ? 'undo_removed_keyword'
                    : 'undo_removed_keyword_unlogged',
                  category: gaEventCategory,
                  label: gaEventLabel,
                });
                mutateAddKeyword({ keywordDisplay, showToastOnSuccess: false });
              },
            },
          });
        }
      },
    }
  );

  return {
    addOfferWishlistKeyword: ({
      checkGeneric = false,
      keywordDisplay,
      keywordMax = null,
    }) =>
      /**
       * `mutateRemoveKeyword` is passed as a param to prevent
       * a circular dependency.
       */
      mutateAddKeyword({
        checkGeneric,
        keywordDisplay,
        keywordMax,
        mutateRemoveKeyword,
      }),
    removeOfferWishlistKeyword: mutateRemoveKeyword,
    offerWishlist,
  };
}

export default useWishlist;
